/**
 * Yjs board-document structure — docs/04 §2 (amended per docs/07 C1/C2).
 *
 * Load-bearing rules encoded here:
 *  - Stable IDs for everything; order and geometry are properties, never identity.
 *  - Order = fractional `ord` keys (LWW map values). Never an ordered Y.Array. (C1)
 *  - One CRDT key per independently-editable property: cells store `v` / `st`
 *    and EACH format property (`bg` first — F-17-bg) under FLAT per-property
 *    keys (`"rowId:colId:prop"` — see `cellKey` for why not a lazily-created
 *    nested map); elements store `x`/`y`/`w`/`h`/`z`/`rotation` separately,
 *    with `anchor` and `payload` as whole-object keys (single semantic units)
 *    and `type` immutable. (C2)
 *  - 2026-07-13 (docs/04 §2, dated 2026-07-10): the single whole-object
 *    `"rowId:colId:fmt"` key is RETIRED — it was a C2 violation waiting to
 *    happen (value-vs-format merged only because `fmt` was one key; two
 *    concurrent writes to different format properties would still LWW each
 *    other away). Legacy `fmt` entries in existing docs remain readable
 *    through a non-destructive shim (see `legacyCellFmtKey`); new writes go
 *    to per-property keys only.
 *
 * This module owns the raw Yjs layout (map names, key formats, typed getters).
 * Feature code must never touch raw Y types — it goes through `BoardDoc`
 * (./board.ts) and the guarded writers (./guards.ts), which are built on top of
 * these primitives.
 */
import * as Y from "yjs";
/** meta.schemaVersion — gate for anything non-additive (docs/08 §2). */
export const SCHEMA_VERSION = 1;
/* ------------------------------------------------------------------ */
/* Root map names                                                      */
/* ------------------------------------------------------------------ */
export const ROOT_META = "meta";
export const ROOT_SHEETS = "sheets";
export const ROOT_ELEMENTS = "elements";
export const ROOT_CONNECTORS = "connectors";
export const ROOT_COMMENTS = "comments";
/** Name of the per-sheet column-meta root map. */
export function colMetaMapName(sheetId) {
    return `sheet/${sheetId}/colMeta`;
}
/** Name of the per-sheet row-meta root map. */
export function rowMetaMapName(sheetId) {
    return `sheet/${sheetId}/rowMeta`;
}
/** Name of the per-sheet cells root map. */
export function cellsMapName(sheetId) {
    return `sheet/${sheetId}/cells`;
}
/* ------------------------------------------------------------------ */
/* Cell keys                                                           */
/* ------------------------------------------------------------------ */
/**
 * Cell ADDRESS `"<rowId>:<colId>"` — used by UI code (grid damage, soft-lock
 * claims). Ids must not contain `":"`.
 *
 * Storage note (C2): cell properties are stored as FLAT per-property keys in
 * the per-sheet cells map — `"<rowId>:<colId>:<prop>"` with prop ∈ v | st |
 * ty | one key per format property (`bg`, `bold`, …) — NOT as a nested Y.Map per
 * cell. A lazily-created nested map loses one side's write when two clients
 * create the same cell entry concurrently (Yjs map-key LWW deletes the
 * losing subtree): A typing a value while B colors the same untouched cell
 * would silently drop one edit — the exact failure C2 forbids. Flat keys
 * make first-write races on different properties commute; same-property
 * races stay plain LWW (C7).
 */
export function cellKey(rowId, colId) {
    return `${rowId}:${colId}`;
}
export function parseCellKey(key) {
    const i = key.indexOf(":");
    if (i <= 0 || i === key.length - 1)
        return null;
    return { rowId: key.slice(0, i), colId: key.slice(i + 1) };
}
export const CELL_FORMAT_PROPS = [
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "bg",
    "align",
];
const MARK_PROP_BY_KIND = {
    bold: "mkBold",
    strike: "mkStrike",
    color: "mkColor",
    hl: "mkHl",
};
/** Storage prop for a mark kind. */
export function cellMarkProp(kind) {
    return MARK_PROP_BY_KIND[kind];
}
export const CELL_MARK_PROPS = ["mkBold", "mkStrike", "mkColor", "mkHl"];
export const CELL_PROPS = [
    "v",
    "st",
    "ty",
    ...CELL_FORMAT_PROPS,
    ...CELL_MARK_PROPS,
];
export const LEGACY_CELL_FMT_PROP = "fmt";
const STORED_CELL_PROPS = new Set([
    ...CELL_PROPS,
    LEGACY_CELL_FMT_PROP,
]);
/** Storage key of one cell property: `"<rowId>:<colId>:<prop>"`. */
export function cellPropKey(rowId, colId, prop) {
    return `${rowId}:${colId}:${prop}`;
}
/** Storage key of the retired whole-object format entry (read-compat only). */
export function legacyCellFmtKey(rowId, colId) {
    return `${rowId}:${colId}:${LEGACY_CELL_FMT_PROP}`;
}
/**
 * Parse a cells-map storage key back into address + property. Accepts the
 * retired legacy `fmt` prop so sweep/compaction code (docs/04 §2) can still
 * account for every stored key.
 */
export function parseCellPropKey(key) {
    const j = key.lastIndexOf(":");
    if (j <= 0)
        return null;
    const prop = key.slice(j + 1);
    if (!STORED_CELL_PROPS.has(prop))
        return null;
    const address = parseCellKey(key.slice(0, j));
    if (!address)
        return null;
    return { rowId: address.rowId, colId: address.colId, prop: prop };
}
/**
 * Text stand-in for an image cell wherever cell content must leave the doc
 * as plain text: grid clipboard copy (F-16) and CSV export (F-91). Exporting
 * the asset URL instead would paste a broken relative link (and, in dev,
 * embed the copier's identity); exporting "" would silently hide that the
 * cell held anything. Presentation-only — never written back as `v`, and
 * distinct from `getCellText` (which stays "" for images: it feeds C5
 * content hashing, where an image has no text value).
 */
export const IMAGE_CELL_TEXT_PLACEHOLDER = "[image]";
/** Runtime guard for image cell content (doc values are untyped `unknown`). */
export function isImageCellValue(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (v.kind === "image" &&
        typeof v.assetId === "string" &&
        typeof v.w === "number" &&
        typeof v.h === "number");
}
/** Longest stem title accepted — the grid renders it as ordinary cell text. */
export const BGM_TITLE_MAX_LENGTH = 200;
/**
 * How many stems a mood run needs before it counts as covered (F-139): a
 * review means choosing, and one candidate is nothing to choose between.
 */
export const BGM_MIN_STEMS = 2;
/** Runtime guard for one stem — strict about the source/payload pairing. */
export function isBgmStem(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    if (typeof v.title !== "string" || v.title.length === 0)
        return false;
    if (v.durationMs !== undefined && typeof v.durationMs !== "number")
        return false;
    if (v.source === "url")
        return typeof v.url === "string" && v.url.length > 0;
    if (v.source === "asset")
        return typeof v.assetId === "string" && v.assetId.length > 0;
    return false;
}
/**
 * Runtime guard for BGM cell content (doc values are untyped `unknown`).
 * Deliberately strict: a `{source:"url"}` with no `url` would render a play
 * button that plays nothing, and one bad alternate fails the whole value for
 * the same reason (a partially valid list is not something a slot can show
 * honestly).
 */
export function isBgmCellValue(value) {
    if (typeof value !== "object" || value === null)
        return false;
    if (value.kind !== "bgm")
        return false;
    if (!isBgmStem(value))
        return false;
    const alternates = value.alternates;
    if (alternates === undefined)
        return true;
    return Array.isArray(alternates) && alternates.every(isBgmStem);
}
/** Every stem in a slot, first candidate first. */
export function bgmStemsOf(value) {
    const first = { source: value.source, title: value.title };
    if (value.url !== undefined)
        first.url = value.url;
    if (value.assetId !== undefined)
        first.assetId = value.assetId;
    if (value.durationMs !== undefined)
        first.durationMs = value.durationMs;
    return [first, ...(value.alternates ?? [])];
}
/** The slot's text — every candidate's title, so CSV and the grid tell the whole list. */
export function bgmCellText(value) {
    return bgmStemsOf(value)
        .map((stem) => stem.title)
        .join(" · ");
}
/**
 * The columns "divide into sections" mints, by NAME rather than colId.
 *
 * Names, because there is no section entity to hold an id: `applySections`
 * writes plain columns, and everything downstream — the BGM coverage check on
 * both client and server, the sections overlay — finds them again by name.
 * Shared (not client-local) because the SERVER has to locate the run column
 * and "BGM" to derive the stems tag against the live doc.
 *
 * Since F-139 the division writes ONE labeled column, "Mood" (the mood as
 * text, the topic as the cell's fill color), plus "BGM". "Section" is what
 * boards divided before F-139 carry; `bgmColumns` still reads it.
 */
export const SECTION_COLUMN_NAME = "Section";
export const MOOD_COLUMN_NAME = "Mood";
export const BGM_COLUMN_NAME = "BGM";
/**
 * A cell's content as PLAIN TEXT, for every consumer that needs a string: the
 * grid's rendered label, F-26 width/height measurement, clipboard copy and
 * CSV export.
 *
 * One function rather than the `isImageCellValue(v) ? … : String(v)` ternary
 * repeated at each call site — which is how a new whole-object cell value
 * (this is the second) reaches production rendering as "[object Object]" in
 * whichever site the author missed.
 *
 * Images have no text (they are elements now); a BGM slot's text is its
 * stems' titles.
 */
export function cellPlainText(value) {
    if (value === undefined || value === null)
        return "";
    if (value instanceof Y.Text)
        return value.toString();
    if (isImageCellValue(value))
        return "";
    if (isBgmCellValue(value))
        return bgmCellText(value);
    return String(value);
}
/* ------------------------------------------------------------------ */
/* Translation mapping (F-70) — colMeta `tx` key                       */
/* ------------------------------------------------------------------ */
/** `ColumnTranslation.from` value meaning "auto-detect from the source column". */
export const TRANSLATION_AUTO_LANG = "auto";
/**
 * BCP-47-ish language tag syntax: a 2–8 letter primary subtag, then any number
 * of alphanumeric subtags — "en", "zh-Hant", "pt-BR", "es-419". Deliberately a
 * SYNTAX check, not the curated dropdown list (that stays a UI concern — see
 * the client's `languages.ts`), so a tag outside the list is still usable while
 * the shape stays boring.
 *
 * Load-bearing for more than tidiness (F-70): `from`/`to` are interpolated into
 * the translation SYSTEM prompt, so this charset — no spaces, no punctuation,
 * no newlines — is what stops a caller-supplied "language" from carrying a
 * sentence that rewrites the prompt's rules. Cell CONTENT cannot escape (it is
 * JSON.stringify'd, and the rowId key-set check catches a steered model), but
 * these two strings land in the instruction itself, so the route validates them
 * here before they ever reach the provider.
 */
export const LANGUAGE_TAG_PATTERN = "^[A-Za-z]{2,8}(-[A-Za-z0-9]{1,8})*$";
const LANGUAGE_TAG_RE = new RegExp(LANGUAGE_TAG_PATTERN);
/** Max length of an accepted language tag — a real BCP-47 tag is far shorter. */
export const LANGUAGE_TAG_MAX_LENGTH = 40;
/**
 * Is `tag` a syntactically valid language tag? `allowAuto` additionally accepts
 * `TRANSLATION_AUTO_LANG` — true for a translation SOURCE (F-70's default),
 * false for a target (there is no "translate into auto").
 */
export function isLanguageTag(tag, allowAuto = false) {
    if (allowAuto && tag === TRANSLATION_AUTO_LANG)
        return true;
    return tag.length <= LANGUAGE_TAG_MAX_LENGTH && LANGUAGE_TAG_RE.test(tag);
}
/** Runtime guard for a stored translation mapping (doc values are untyped). */
export function isColumnTranslation(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (typeof v.from === "string" &&
        typeof v.to === "string" &&
        typeof v.updatedAt === "number" &&
        (v.model === undefined || typeof v.model === "string"));
}
/* ------------------------------------------------------------------ */
/* Translation JOB state (F-70 async) — meta `txJob` key                */
/* ------------------------------------------------------------------ */
/**
 * `meta` key holding the live translation-job state (F-70, amended
 * 2026-07-16). It lives ON THE BOARD DOC rather than in a REST/polling
 * endpoint because the requirement is that *every* client sees the same
 * progress bar, live, and that a user who reloads mid-job still sees it — the
 * doc is already the broadcast channel, so the job needs no second transport.
 */
export const TRANSLATION_JOB_KEY = "txJob";
/**
 * Runtime guard for stored job state (doc values are untyped `unknown`). A
 * half-valid object reads as "no job" rather than driving a progress bar from
 * `NaN`.
 */
export function isTranslationJobState(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (typeof v.jobId === "string" &&
        typeof v.sheetId === "string" &&
        typeof v.targetColId === "string" &&
        (v.status === "running" ||
            v.status === "done" ||
            v.status === "cancelled" ||
            v.status === "error") &&
        typeof v.done === "number" &&
        typeof v.total === "number" &&
        typeof v.failed === "number" &&
        typeof v.startedAt === "number" &&
        (v.message === undefined || typeof v.message === "string"));
}
/* ------------------------------------------------------------------ */
/* Design theme (mood board) — the `design` root map                    */
/* ------------------------------------------------------------------ */
/**
 * The board's ONE design theme: the art direction for the whole video,
 * generated from its Original Script (F-28) and then edited by hand.
 *
 * Lives in its own root map rather than on `meta` because it is a document of
 * its own — a dozen independently-edited fields plus two growing collections
 * (`design/refs`, `design/shots`) — and `meta` is board chrome that every
 * client observes for renames and job ticks. Additive: an old client that has
 * never heard of `design` simply never calls `getDesign` (docs/08 §1), so no
 * `schemaVersion` bump.
 *
 * SCOPE: one theme per board = one video. Per-row (script-by-script) design is
 * a later step and will hang off `design/shots` entries carrying a cell
 * `Anchor`, not off these keys.
 */
export const ROOT_DESIGN = "design";
/** `design/refs`: refId → style-reference frame the user uploaded. */
export function designRefsMapName() {
    return "design/refs";
}
/** `design/shots`: shotId → one generated key visual (the gallery). */
export function designShotsMapName() {
    return "design/shots";
}
/**
 * The user's own instruction, prepended to every prompt ("this is a 3-minute
 * explainer, keep it warmer than usual"). Their words, never the model's — the
 * generator reads it and never writes it.
 */
export const DESIGN_BRIEF_KEY = "brief";
/** Model that produced the current art direction (provenance, shown in the panel). */
export const DESIGN_MODEL_KEY = "ad:model";
/** When the art direction was last written, by anyone (ms since epoch). */
export const DESIGN_UPDATED_AT_KEY = "ad:updatedAt";
/**
 * The free-text art-direction fields, each its own CRDT key (C2) so two people
 * can sharpen the palette note and the motion note at the same time without
 * either losing the other's words.
 *
 * The set is deliberately opinionated rather than a generic "notes" blob: these
 * are the questions an animator actually has to answer before boarding, and
 * naming them is what stops the model from returning three paragraphs of mood
 * adjectives. `avoid` earns its place — "minimal" and "authentic" fail in
 * predictable ways (gradient mush, plastic 3D render, corporate memphis), and
 * an explicit anti-brief is the cheapest correction for that.
 */
export const ART_DIRECTION_TEXT_FIELDS = [
    "logline",
    "mood",
    "perspective",
    "lighting",
    "texture",
    "motion",
    "type",
    "avoid",
];
/** Storage key for one art-direction text field. */
export function artDirectionKey(field) {
    return `ad:${field}`;
}
/**
 * Whole-object key, unlike the text fields: a palette is chosen as a SET (the
 * accent only means anything against the background it sits on), so per-swatch
 * keys would let two people merge half of one palette into half of another and
 * produce a combination neither picked. Same reasoning as an element's
 * `anchor`/`payload`.
 */
export const DESIGN_PALETTE_KEY = "ad:palette";
/** How many swatches a palette may hold — a palette, not a spectrum. */
export const MAX_PALETTE_SWATCHES = 8;
/**
 * `meta`-style key on the design map holding the live image-job state.
 *
 * Same reasoning as F-70's `txJob`, which this deliberately mirrors: ONE
 * whole-object key, written only by the server's job loop under a machine
 * origin, read-only for clients. One key = one atomic progress snapshot, so a
 * reader can never see `done` from one run and `total` from another.
 */
export const DESIGN_JOB_KEY = "imgJob";
/** Runtime guard for stored job state (doc values are untyped `unknown`). */
export function isDesignJobState(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (typeof v.jobId === "string" &&
        typeof v.runId === "string" &&
        (v.status === "running" ||
            v.status === "done" ||
            v.status === "cancelled" ||
            v.status === "error") &&
        typeof v.done === "number" &&
        typeof v.total === "number" &&
        typeof v.failed === "number" &&
        typeof v.startedAt === "number" &&
        (v.message === undefined || typeof v.message === "string"));
}
export function isPaletteSwatch(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return typeof v.hex === "string" && typeof v.name === "string";
}
/**
 * Runtime guard for a stored palette (doc values are untyped). A half-valid
 * array reads as "no palette" rather than rendering swatches from `undefined`.
 */
export function isPalette(value) {
    return Array.isArray(value) && value.every(isPaletteSwatch);
}
/* ------------------------------------------------------------------ */
/* Thumbnails (F-130) — the `thumbs` root map                          */
/* ------------------------------------------------------------------ */
/**
 * The board's YouTube thumbnails: the words the team wants on them, the
 * portraits they are built from, and every frame the generator has made.
 *
 * Its own root map, beside `design`, for the same reason `design` is beside
 * `meta`: it is a document of its own (a handful of independently-edited
 * settings plus two growing collections), and a client that predates it
 * simply never calls `getThumbs`. Additive — no `schemaVersion` bump.
 *
 * WHAT IS SHARED AND WHAT IS NOT. The title, the hook text, the model picks
 * and the portraits are BOARD content — one video has one presenter and one
 * set of words, and two people refining thumbnails together must be looking
 * at the same ones. That is why the model picks live here rather than in the
 * per-reader localStorage the brainstorm picker uses: a thumbnail run costs
 * real money, and "which model made these" has to be the same answer for
 * everyone who looks at the gallery.
 */
export const ROOT_THUMBS = "thumbs";
/** `thumbs/portraits`: portraitId → an uploaded face the thumbnails feature. */
export function thumbPortraitsMapName() {
    return "thumbs/portraits";
}
/** `thumbs/shots`: shotId → one generated thumbnail (first round or refined). */
export function thumbShotsMapName() {
    return "thumbs/shots";
}
/** The video title the thumbnails pair with. The user's, or the last run's pick. */
export const THUMB_TITLE_KEY = "title";
/** The hook text lettered ON the thumbnail (Traditional Chinese, Hong Kong). */
export const THUMB_TEXT_KEY = "text";
/** Image model for the render pass; absent = the server's default. */
export const THUMB_IMAGE_MODEL_KEY = "imageModel";
/** Text model that writes the hooks; absent = the server's default. */
export const THUMB_TEXT_MODEL_KEY = "textModel";
/** The shot the team picked — one per board, or none. */
export const THUMB_SELECTED_KEY = "selected";
/** Live run state (one whole-object key, server-written — see `DESIGN_JOB_KEY`). */
export const THUMB_JOB_KEY = "job";
/** Thumbnails one run renders — five distinct hooks to choose between. */
export const THUMBNAIL_RUN_COUNT = 5;
/** Portraits one board may hold: the presenter plus a few guests. */
export const MAX_THUMB_PORTRAITS = 4;
/** Hard cap on the hook text — a thumbnail carries a phrase, not a sentence. */
export const THUMB_TEXT_MAX_LENGTH = 40;
export const THUMB_TITLE_MAX_LENGTH = 120;
/** A portrait's caption ("guest — Ms Chan") — prompt context, never lettered. */
export const THUMB_PORTRAIT_LABEL_MAX_LENGTH = 60;
/** Runtime guard for stored job state (doc values are untyped `unknown`). */
export function isThumbJobState(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (typeof v.jobId === "string" &&
        typeof v.runId === "string" &&
        (v.status === "running" ||
            v.status === "done" ||
            v.status === "cancelled" ||
            v.status === "error") &&
        typeof v.done === "number" &&
        typeof v.total === "number" &&
        typeof v.failed === "number" &&
        typeof v.startedAt === "number" &&
        (v.phase === undefined || v.phase === "writing" || v.phase === "rendering") &&
        (v.message === undefined || typeof v.message === "string"));
}
/** Runtime guard for a link-card payload (doc payload values are untyped). */
export function isLinkCardElementPayload(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (v.kind === "linkCard" &&
        typeof v.url === "string" &&
        (v.status === "loading" || v.status === "ready" || v.status === "error"));
}
/** A usable clip bound: a finite, non-negative number of milliseconds. */
function isClipMs(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
/**
 * The clip range a link-card payload carries, NORMALIZED: malformed bounds
 * are dropped, an inverted or empty pair (`end <= start`) keeps only its
 * start (the writer's more deliberate half — an end typed before the start
 * was corrected is the usual way to get here), and a payload with neither
 * yields undefined so callers can `if (range)`. Readers use this, never the
 * raw fields: the doc is shared and any client may have written them.
 */
export function clipRangeOf(payload) {
    const start = isClipMs(payload.clipStartMs) ? Math.round(payload.clipStartMs) : undefined;
    let end = isClipMs(payload.clipEndMs) ? Math.round(payload.clipEndMs) : undefined;
    if (start !== undefined && end !== undefined && end <= start)
        end = undefined;
    if (start === undefined && end === undefined)
        return undefined;
    const range = {};
    if (start !== undefined)
        range.startMs = start;
    if (end !== undefined)
        range.endMs = end;
    return range;
}
/**
 * `payload` with its clip range replaced (undefined = cleared). Returns a NEW
 * object with every other field untouched, so the caller writes it back as
 * the whole `payload` key. Normalizes through `clipRangeOf` so what lands in
 * the doc is what every reader would derive anyway.
 */
export function withClipRange(payload, range) {
    const { clipStartMs: _s, clipEndMs: _e, ...rest } = payload;
    const next = { ...rest };
    const normalized = range === undefined
        ? undefined
        : clipRangeOf({ ...rest, clipStartMs: range.startMs, clipEndMs: range.endMs });
    if (normalized?.startMs !== undefined)
        next.clipStartMs = normalized.startMs;
    if (normalized?.endMs !== undefined)
        next.clipEndMs = normalized.endMs;
    return next;
}
/** Runtime guard for a drawing payload (doc payload values are untyped). */
export function isDrawingElementPayload(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (v.kind === "drawing" &&
        Array.isArray(v.d) &&
        v.d.every((n) => typeof n === "number" && Number.isFinite(n)) &&
        typeof v.color === "string" &&
        typeof v.thickness === "number");
}
/** Runtime guard for a file payload (doc payload values are untyped). */
export function isFileElementPayload(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return (v.kind === "file" &&
        typeof v.assetId === "string" &&
        typeof v.name === "string" &&
        typeof v.contentType === "string" &&
        typeof v.size === "number" &&
        (v.media === "video" || v.media === "audio" || v.media === "file"));
}
/** Clockwise from the top — the order ports are enumerated/rendered in. */
export const CONNECTOR_SIDES = ["top", "right", "bottom", "left"];
export function isConnectorSide(value) {
    return (value === "top" || value === "right" || value === "bottom" || value === "left");
}
/** Runtime guard for a stored endpoint (doc values are untyped). */
export function isConnectorEndpoint(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return typeof v.elementId === "string" && v.elementId.length > 0 && isConnectorSide(v.side);
}
export function getMeta(doc) {
    return doc.getMap(ROOT_META);
}
export function getSheets(doc) {
    return doc.getMap(ROOT_SHEETS);
}
export function getColMeta(doc, sheetId) {
    return doc.getMap(colMetaMapName(sheetId));
}
export function getRowMeta(doc, sheetId) {
    return doc.getMap(rowMetaMapName(sheetId));
}
export function getCells(doc, sheetId) {
    return doc.getMap(cellsMapName(sheetId));
}
export function getElements(doc) {
    return doc.getMap(ROOT_ELEMENTS);
}
export function getConnectors(doc) {
    return doc.getMap(ROOT_CONNECTORS);
}
export function getComments(doc) {
    return doc.getMap(ROOT_COMMENTS);
}
export function getDesign(doc) {
    return doc.getMap(ROOT_DESIGN);
}
export function getDesignRefs(doc) {
    return doc.getMap(designRefsMapName());
}
export function getDesignShots(doc) {
    return doc.getMap(designShotsMapName());
}
export function getThumbs(doc) {
    return doc.getMap(ROOT_THUMBS);
}
export function getThumbPortraits(doc) {
    return doc.getMap(thumbPortraitsMapName());
}
export function getThumbShots(doc) {
    return doc.getMap(thumbShotsMapName());
}
/**
 * Idempotently stamp `meta.schemaVersion` — writes only when absent.
 *
 * SERVER-ONLY: must run where the caller has causal knowledge of the stored
 * doc state (Hocuspocus onLoadDocument, after applying the stored update).
 * Never call this on a client's empty pre-sync doc: the stamp would be
 * causally concurrent with the server state, and Yjs map-key LWW (clientID
 * order) could downgrade a future higher schemaVersion — defeating the
 * docs/08 §2 non-additive schema gate.
 */
export function initBoardDoc(doc, origin = null) {
    const meta = getMeta(doc);
    if (meta.get("schemaVersion") === undefined) {
        doc.transact(() => {
            meta.set("schemaVersion", SCHEMA_VERSION);
        }, origin);
    }
}
//# sourceMappingURL=schema.js.map